import {
  type ComponentType,
  type ElementType,
  type FC,
  type MouseEventHandler,
  type ReactNode,
  useEffect,
  useRef,
} from 'react'
import { type SerializedStyles } from '@emotion/react'
import { Dialog as MaterialDialog, type DialogProps as MaterialDialogProps, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import ConditionalWrapper from '@DS/components/components/utils/wrapper/ConditionalWrapper'
import Warning from '@DS/components/illustrations/warning/Warning'
import { useIsMediumUp } from '@Hooks/useResponsive'

import { type DialogAction } from './Dialog.types'
import * as styles from './styles'

export type DialogVariant =
  | {
      variant?: 'default'
      illustration?: ComponentType
    }
  | {
      variant?: 'error_system'
      illustration?: never
    }

export type GenericDialogProps = DialogVariant & {
  show?: boolean
  message: string
  annotations?: ReactNode
  annotationsStyles?: SerializedStyles
  defaultAction: DialogAction
  actions?: [DialogAction] | [DialogAction, DialogAction]
  onClose: VoidFunction
  hasToDisableCloseOnBackdropClick?: boolean
  hasToDisableCloseOnEscapeKeyDown?: boolean
  hasToDisplayActionsInColumnOnMobile?: boolean
  linkButtonComponent: ElementType<{
    onClick?: MouseEventHandler
    form?: string
    children: ReactNode
  }>
  externalLinkComponent: ElementType<{ href?: string; children: ReactNode }>
} & Omit<MaterialDialogProps, 'open' | 'onClose'>

const GenericDialog: FC<GenericDialogProps> = ({
  show = false,
  message,
  annotations,
  annotationsStyles,
  variant = 'default',
  illustration: Illustration = variant === 'error_system' && Warning,
  defaultAction,
  actions = [],
  onClose,
  hasToDisableCloseOnBackdropClick = false,
  hasToDisableCloseOnEscapeKeyDown = false,
  hasToDisplayActionsInColumnOnMobile = false,
  linkButtonComponent: LinkButtonComponent,
  externalLinkComponent: ExternalLinkComponent,
  ...rest
}) => {
  const theme = useTheme()
  const isDesktop = useIsMediumUp(theme)

  const dialogRef = useRef<HTMLDivElement>(null)

  const handleClose = (reason: string) => {
    if (
      (reason === 'backdropClick' && hasToDisableCloseOnBackdropClick) ||
      (reason === 'escapeKeyDown' && hasToDisableCloseOnEscapeKeyDown)
    ) {
      return
    }

    onClose()
  }

  const shouldDisplayActionsInColumnOnMobile =
    (hasToDisplayActionsInColumnOnMobile || actions.length >= 2) && !isDesktop

  useEffect(() => {
    dialogRef.current?.focus()
  }, [])

  return (
    <MaterialDialog
      {...rest}
      slotProps={{
        paper: {
          elevation: 0,
        },
      }}
      open={show}
      scroll="paper"
      maxWidth="sm"
      fullWidth
      onClose={(event, reason) => handleClose(reason)}
      data-test="dialog"
    >
      <div css={styles.content(theme)} ref={dialogRef} tabIndex={-1}>
        {Illustration && <Illustration css={styles.illustration(theme)} mode="simple" size="small" />}
        <Typography variant="body1" data-test="message">
          {message}
        </Typography>
        {annotations && (
          <Typography css={annotationsStyles || styles.annotations(theme)} data-test="annotations" component="span">
            {annotations}
          </Typography>
        )}
        <div css={styles.actions(theme)}>
          <div css={styles.linksWrapper(shouldDisplayActionsInColumnOnMobile)}>
            <ConditionalWrapper
              condition={!!defaultAction.href}
              wrapper={(children) => (
                <ExternalLinkComponent data-test="default-action" href={defaultAction.href}>
                  {children}
                </ExternalLinkComponent>
              )}
              defaultWrapper={(children) => (
                <LinkButtonComponent
                  data-test="default-action"
                  onClick={() => {
                    defaultAction.onClick?.()
                    onClose()
                  }}
                  form={defaultAction.form}
                >
                  {children}
                </LinkButtonComponent>
              )}
            >
              {defaultAction.label}
            </ConditionalWrapper>
            {actions.map(({ href, onClick, form, label }) => (
              <ConditionalWrapper
                key={label}
                condition={!!href}
                wrapper={(children) => (
                  <ExternalLinkComponent data-test="next-action" href={href}>
                    {children}
                  </ExternalLinkComponent>
                )}
                defaultWrapper={(children) => (
                  <LinkButtonComponent
                    data-test="next-action"
                    onClick={() => {
                      onClick?.()
                      onClose()
                    }}
                    form={form}
                  >
                    {children}
                  </LinkButtonComponent>
                )}
              >
                {label}
              </ConditionalWrapper>
            ))}
          </div>
        </div>
      </div>
    </MaterialDialog>
  )
}

export default GenericDialog
